import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Home, Inbox, FileText, Users, Briefcase, LayoutDashboard, Loader2, Plus, Trash2, CheckCircle2, X, Mail, Phone, Building2, Edit3, ChevronDown, ChevronRight, Award } from "lucide-react";
import { auth, enquiriesApi, blogApi, clientsApi, projectsApi, statsApi } from "../lib/api";
import { COMPANY } from "../data/mock";
import ProjectDetail from "../components/ProjectDetail";
import CaseStudiesCMS from "../components/CaseStudiesCMS";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "enquiries", label: "Enquiries", icon: Inbox },
  { key: "blog", label: "Blog CMS", icon: FileText },
  { key: "cases", label: "Case Studies", icon: Award },
  { key: "clients", label: "Clients", icon: Users },
  { key: "projects", label: "Projects", icon: Briefcase },
];

const fmt = (iso) => { try { return new Date(iso).toLocaleString("en-IN"); } catch { return iso; } };

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [badge, setBadge] = useState(0);
  const user = auth.getUser();

  useEffect(() => {
    if (!user || user.role !== "admin") navigate("/admin/login");
  }, [user, navigate]);

  // Poll new-enquiry badge every 20s
  useEffect(() => {
    if (!user || user.role !== "admin") return;
    let cancelled = false;
    const load = async () => {
      try {
        const s = await statsApi.admin();
        if (!cancelled) setBadge(s.new_enquiries || 0);
      } catch {}
    };
    load();
    const t = setInterval(load, 20000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user, tab]);

  const logout = () => { auth.clear(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200">
        <div className="lis-container flex items-center justify-between py-3">
          <Link to="/" className="flex items-center gap-3">
            <img src={COMPANY.logo} alt="LIS" className="h-14" />
            <div>
              <div className="text-xs text-slate-500">Admin Console</div>
              <div className="font-bold text-slate-900">LIINEX INFORMATION SYSTEMS</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-slate-600 hover:text-brand hidden sm:inline-flex items-center gap-1"><Home size={14}/> View Site</Link>
            <div className="text-right text-xs">
              <div className="font-semibold text-slate-900">{user?.name}</div>
              <div className="text-slate-500">{user?.email}</div>
            </div>
            <button onClick={logout} className="btn-outline text-sm"><LogOut size={14}/> Logout</button>
          </div>
        </div>
      </div>

      <div className="lis-container py-6 grid lg:grid-cols-[220px_1fr] gap-6">
        <aside className="bg-white rounded-xl border border-slate-100 p-3 h-fit sticky top-4">
          <nav className="flex lg:flex-col gap-1">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              const showBadge = t.key === "enquiries" && badge > 0;
              return (
                <button key={t.key} onClick={() => setTab(t.key)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left relative ${active ? "bg-red-50 text-brand" : "text-slate-700 hover:bg-slate-50"}`}>
                  <Icon size={16}/> <span>{t.label}</span>
                  {showBadge && (
                    <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-brand text-white text-[11px] font-bold shadow">
                      {badge > 99 ? "99+" : badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </aside>

        <main>
          {tab === "overview" && <Overview />}
          {tab === "enquiries" && <Enquiries />}
          {tab === "blog" && <BlogCMS />}
          {tab === "cases" && <CaseStudiesCMS />}
          {tab === "clients" && <ClientsAdmin />}
          {tab === "projects" && <ProjectsAdmin />}
        </main>
      </div>
    </div>
  );
};

const Card = ({ title, value, sub }) => (
  <div className="bg-white rounded-xl p-5 border border-slate-100">
    <div className="text-xs text-slate-500 uppercase tracking-wider">{title}</div>
    <div className="mt-2 text-3xl font-extrabold text-brand">{value}</div>
    {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
  </div>
);

const Overview = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => { statsApi.admin().then(setStats).catch(() => {}); }, []);
  if (!stats) return <div className="p-6"><Loader2 className="animate-spin"/></div>;
  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-900">Welcome back </h2>
      <p className="text-sm text-slate-600 mt-1">A quick view of activity across your workspace.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-6">
        <Card title="Total Enquiries" value={stats.enquiries} sub={`${stats.new_enquiries} new`} />
        <Card title="Clients" value={stats.clients} />
        <Card title="Active Projects" value={stats.projects} />
        <Card title="Blog Posts" value={stats.blog_posts} />
        <Card title="Case Studies" value={stats.case_studies ?? 0} />
      </div>
    </div>
  );
};

const Enquiries = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = () => {
    setLoading(true);
    enquiriesApi.list(filter || undefined).then(setItems).finally(() => setLoading(false));
  };
  useEffect(load, [filter]);

  const setStatus = async (id, status) => { await enquiriesApi.updateStatus(id, status); load(); };
  const del = async (id) => { if (window.confirm("Delete this enquiry?")) { await enquiriesApi.remove(id); load(); } };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-900">Enquiries</h2>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="contact-input max-w-[180px]">
          <option value="">All statuses</option>
          <option value="new">New</option>
          <option value="contacted">Contacted</option>
          <option value="won">Won</option>
          <option value="closed">Closed</option>
        </select>
      </div>
      {loading ? <div className="mt-6"><Loader2 className="animate-spin"/></div> : (
        <div className="mt-6 space-y-3">
          {items.length === 0 && <div className="text-slate-500 text-sm">No enquiries yet.</div>}
          {items.map((e) => (
            <div key={e.id} className="bg-white rounded-xl border border-slate-100 p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-900">{e.name}</h3>
                    <span className="chip">{e.service || "General"}</span>
                    <span className={`text-[11px] uppercase font-bold px-2 py-1 rounded ${e.status === "new" ? "bg-emerald-50 text-emerald-700" : e.status === "won" ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-700"}`}>{e.status}</span>
                  </div>
                  <div className="mt-2 text-sm text-slate-600 flex flex-wrap gap-4">
                    <span className="flex items-center gap-1"><Mail size={12}/> {e.email}</span>
                    <span className="flex items-center gap-1"><Phone size={12}/> {e.phone}</span>
                    {e.company && <span className="flex items-center gap-1"><Building2 size={12}/> {e.company}</span>}
                  </div>
                  {e.message && <p className="mt-3 text-sm text-slate-700 bg-slate-50 p-3 rounded-lg">{e.message}</p>}
                  <div className="mt-2 text-xs text-slate-400">{fmt(e.created_at)}</div>
                </div>
                <div className="flex flex-col gap-2">
                  <select value={e.status} onChange={(ev) => setStatus(e.id, ev.target.value)} className="contact-input text-sm">
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="won">Won</option>
                    <option value="closed">Closed</option>
                  </select>
                  <button onClick={() => del(e.id)} className="btn-outline text-sm text-red-600 hover:border-red-500"><Trash2 size={14}/></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const emptyPost = { title: "", excerpt: "", content: "", tag: "General", image: "", author: "LIS Editorial", published: true };

const BlogCMS = () => {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPost);
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); blogApi.listAll().then(setPosts).finally(() => setLoading(false)); };
  useEffect(load, []);

  const save = async (e) => {
    e.preventDefault();
    if (editing) await blogApi.update(editing, form); else await blogApi.create(form);
    setEditing(null); setForm(emptyPost); load();
  };
  const edit = (p) => { setEditing(p.slug); setForm({ title: p.title, excerpt: p.excerpt, content: p.content, tag: p.tag, image: p.image, author: p.author, published: p.published }); };
  const del = async (slug) => { if (window.confirm("Delete this post?")) { await blogApi.remove(slug); load(); } };

  return (
    <div>
      <div className="flex items-center justify-between"><h2 className="text-2xl font-bold text-slate-900">Blog CMS</h2>
        {editing && <button onClick={() => { setEditing(null); setForm(emptyPost); }} className="btn-outline text-sm"><X size={14}/> Cancel edit</button>}
      </div>

      <form onSubmit={save} className="bg-white border border-slate-100 rounded-xl p-5 mt-4 grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2"><label className="text-xs font-semibold">Title*</label>
          <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="contact-input" /></div>
        <div><label className="text-xs font-semibold">Tag</label>
          <input value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} className="contact-input" /></div>
        <div><label className="text-xs font-semibold">Author</label>
          <input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className="contact-input" /></div>
        <div className="sm:col-span-2"><label className="text-xs font-semibold">Image URL</label>
          <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="contact-input" placeholder="https://..." /></div>
        <div className="sm:col-span-2"><label className="text-xs font-semibold">Excerpt*</label>
          <input required value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="contact-input" /></div>
        <div className="sm:col-span-2"><label className="text-xs font-semibold">Content*</label>
          <textarea required rows={6} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="contact-input" /></div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })}/> Published</label>
        <button type="submit" className="btn-brand justify-center sm:col-span-2">{editing ? "Update Post" : "Publish Post"} <CheckCircle2 size={14}/></button>
      </form>

      <h3 className="mt-8 font-semibold text-slate-900">All Posts ({posts.length})</h3>
      {loading ? <Loader2 className="animate-spin mt-4"/> : (
        <div className="mt-3 space-y-2">
          {posts.map((p) => (
            <div key={p.slug} className="bg-white rounded-xl border border-slate-100 p-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                {p.image && <img src={p.image} alt="" className="w-14 h-14 object-cover rounded-lg"/>}
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{p.title}</div>
                  <div className="text-xs text-slate-500">/{p.slug}  {p.tag}  {p.published ? "Published" : "Draft"}</div>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => edit(p)} className="btn-outline text-sm"><Edit3 size={14}/></button>
                <button onClick={() => del(p.slug)} className="btn-outline text-sm text-red-600 hover:border-red-500"><Trash2 size={14}/></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const emptyClient = { name: "", email: "", company: "", phone: "", password: "" };

const ClientsAdmin = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyClient);
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); clientsApi.list().then(setItems).finally(() => setLoading(false)); };
  useEffect(load, []);

  const create = async (e) => { e.preventDefault(); await clientsApi.create(form); setForm(emptyClient); load(); };
  const del = async (id) => { if (window.confirm("Delete client and all their projects?")) { await clientsApi.remove(id); load(); } };

  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-900">Clients</h2>
      <form onSubmit={create} className="bg-white rounded-xl border border-slate-100 p-5 mt-4 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <input required placeholder="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="contact-input" />
        <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="contact-input" />
        <input placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="contact-input" />
        <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="contact-input" />
        <input required type="text" placeholder="Set password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="contact-input" />
        <button type="submit" className="btn-brand sm:col-span-2 lg:col-span-5 justify-center"><Plus size={14}/> Add Client</button>
      </form>
      {loading ? <Loader2 className="animate-spin mt-4"/> : (
        <div className="mt-6 grid md:grid-cols-2 gap-3">
          {items.length === 0 && <div className="text-slate-500 text-sm">No clients yet. Add one above.</div>}
          {items.map((c) => (
            <div key={c.id} className="bg-white rounded-xl border border-slate-100 p-5 flex items-start justify-between">
              <div>
                <div className="font-semibold text-slate-900">{c.name}</div>
                <div className="text-xs text-slate-500">{c.email}</div>
                <div className="text-xs text-slate-600 mt-1">{c.company}  {c.phone}</div>
                <div className="text-[11px] text-slate-400 mt-1">Created {fmt(c.created_at)}</div>
              </div>
              <button onClick={() => del(c.id)} className="btn-outline text-sm text-red-600"><Trash2 size={14}/></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const emptyProject = { client_id: "", standard: "ISO 9001", scope: "", target_date: "", notes: "" };

const STATUS_COLORS = {
  pending: "bg-slate-100 text-slate-600",
  in_progress: "bg-amber-100 text-amber-700",
  done: "bg-emerald-100 text-emerald-700",
};

const ProjectsAdmin = () => {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(emptyProject);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([projectsApi.list(), clientsApi.list()]).then(([p, c]) => { setProjects(p); setClients(c); }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const create = async (e) => { e.preventDefault(); await projectsApi.create(form); setForm(emptyProject); load(); };
  const del = async (id) => { if (window.confirm("Delete project?")) { await projectsApi.remove(id); load(); } };
  const setMilestone = async (pid, key, status) => { await projectsApi.updateMilestone(pid, { key, status }); load(); };

  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-900">Certification Projects</h2>
      <form onSubmit={create} className="bg-white rounded-xl border border-slate-100 p-5 mt-4 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <select required value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="contact-input">
          <option value="">Select client</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}  {c.company}</option>)}
        </select>
        <select value={form.standard} onChange={(e) => setForm({ ...form, standard: e.target.value })} className="contact-input">
          {["ISO 9001","ISO 14001","ISO 45001","ISO 27001","ISO 22000","ISO 13485","IATF 16949","ISO 17025","ISO 22301","ISO 50001","SOC 2"].map((s) => <option key={s}>{s}</option>)}
        </select>
        <input placeholder="Scope" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} className="contact-input" />
        <input type="date" value={form.target_date || ""} onChange={(e) => setForm({ ...form, target_date: e.target.value })} className="contact-input" />
        <button type="submit" className="btn-brand justify-center"><Plus size={14}/> Add Project</button>
      </form>

      {loading ? <Loader2 className="animate-spin mt-4"/> : (
        <div className="mt-6 space-y-4">
          {projects.length === 0 && <div className="text-slate-500 text-sm">No projects yet.</div>}
          {projects.map((p) => {
            const done = p.milestones.filter((m) => m.status === "done").length;
            const total = p.milestones.length;
            const pct = Math.round((done / total) * 100);
            return (
              <div key={p.id} className="bg-white rounded-xl border border-slate-100 p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-slate-900">{p.standard}</h3>
                      <span className="chip">{p.client_name}</span>
                      {p.client_company && <span className="text-xs text-slate-500">{p.client_company}</span>}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">Target: {p.target_date || "TBD"}  {p.scope}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setExpanded(expanded === p.id ? null : p.id)} className="btn-outline text-sm">
                      {expanded === p.id ? <><ChevronDown size={14}/> Hide</> : <><ChevronRight size={14}/> Files & Chat</>}
                    </button>
                    <button onClick={() => del(p.id)} className="btn-outline text-sm text-red-600"><Trash2 size={14}/></button>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-2 bg-brand transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{done}/{total} milestones  {pct}%</div>
                </div>
                <div className="mt-4 grid sm:grid-cols-2 gap-2">
                  {p.milestones.map((m) => (
                    <div key={m.key} className="flex items-center justify-between gap-2 border border-slate-100 rounded-lg p-2">
                      <div className="text-sm text-slate-800">{m.title}</div>
                      <select value={m.status} onChange={(e) => setMilestone(p.id, m.key, e.target.value)} className={`text-xs font-semibold rounded px-2 py-1 ${STATUS_COLORS[m.status]}`}>
                        <option value="pending">Pending</option>
                        <option value="in_progress">In Progress</option>
                        <option value="done">Done</option>
                      </select>
                    </div>
                  ))}
                </div>
                {expanded === p.id && <ProjectDetail projectId={p.id} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
